import { useEffect, useMemo, useRef, useState } from "react";
import {
  selectAvailableRoleNames,
  selectHMSMessages,
  selectLocalPeerID,
  selectMessagesByPeerID,
  selectMessagesByRole,
  selectPermissions,
  selectRemotePeers,
  selectSessionStore,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Ellipsis, Pin, PinOff, SendHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SidePaneHeader } from "@/components/meeting/SidePaneHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { isInternalRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

export const PINNED_MESSAGE_KEY = "pinnedMessage";
const EVERYONE = "everyone";

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const isUrl = part => /^https?:\/\//.test(part);

const Linkified = ({ text }) =>
  text.split(URL_PATTERN).map((part, index) =>
    isUrl(part) ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-primary underline underline-offset-2"
      >
        {part}
      </a>
    ) : (
      <span key={index}>{part}</span>
    )
  );

const formatTime = date =>
  date
    ? new Date(date).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

const recipientLabel = (message, localPeerId) => {
  if (message.recipientPeer) {
    return message.recipientPeer === localPeerId ? "to you" : "private";
  }
  if (message.recipientRoles?.length) {
    return `to ${message.recipientRoles.join(", ")}`;
  }
  return null;
};

const useMessages = recipient => {
  const all = useHMSStore(selectHMSMessages);
  const byRole = useHMSStore(
    selectMessagesByRole(
      recipient.startsWith("role:") ? recipient.slice(5) : ""
    )
  );
  const byPeer = useHMSStore(
    selectMessagesByPeerID(
      recipient.startsWith("peer:") ? recipient.slice(5) : ""
    )
  );
  if (recipient.startsWith("role:")) {
    return byRole || [];
  }
  if (recipient.startsWith("peer:")) {
    return byPeer || [];
  }
  return (all || []).filter(message => !message.ignored);
};

const PinnedMessage = () => {
  const hmsActions = useHMSActions();
  const pinned = useHMSStore(selectSessionStore(PINNED_MESSAGE_KEY));
  const permissions = useHMSStore(selectPermissions);
  if (!pinned) {
    return null;
  }
  return (
    <div className="flex items-start gap-2 border-b bg-muted/50 px-4 py-2 text-sm">
      <Pin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <p className="min-w-0 flex-1 break-words" data-mask>
        <Linkified text={pinned} />
      </p>
      {permissions?.removeOthers ? (
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Unpin message"
          onClick={() => hmsActions.sessionStore.set(PINNED_MESSAGE_KEY, null)}
        >
          <PinOff />
        </Button>
      ) : null}
    </div>
  );
};

const Message = ({ message, localPeerId, canPin }) => {
  const hmsActions = useHMSActions();
  const isMine = message.sender === localPeerId;
  const recipient = recipientLabel(message, localPeerId);
  const isBroadcast = !message.recipientPeer && !message.recipientRoles?.length;

  return (
    <li className="group flex flex-col gap-1 px-4 py-2" data-testid="chat_msg">
      <div className="flex items-center gap-2 text-xs">
        <span className="max-w-[60%] truncate font-medium">
          {isMine ? "You" : message.senderName || "Anonymous"}
        </span>
        <span className="text-muted-foreground">
          {formatTime(message.time)}
        </span>
        {recipient ? (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-primary uppercase">
            {recipient}
          </span>
        ) : null}
        {canPin && isBroadcast ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label="Message options"
                className="ml-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              >
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="pin_message_btn"
                onSelect={() =>
                  hmsActions.sessionStore.set(
                    PINNED_MESSAGE_KEY,
                    `${message.senderName || "Anonymous"}: ${message.message}`
                  )
                }
              >
                <Pin />
                Pin message
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <p
        className={cn(
          "text-sm break-words whitespace-pre-wrap",
          !isBroadcast && "text-foreground/90"
        )}
        data-mask
      >
        <Linkified text={message.message} />
      </p>
    </li>
  );
};

export const ChatPanel = ({ onClose }) => {
  const hmsActions = useHMSActions();
  const localPeerId = useHMSStore(selectLocalPeerID);
  const permissions = useHMSStore(selectPermissions);
  const remotePeers = useHMSStore(selectRemotePeers).filter(
    peer => !isInternalRole(peer.roleName)
  );
  const roles = (useHMSStore(selectAvailableRoleNames) || []).filter(
    role => !isInternalRole(role)
  );
  const [chosenRecipient, setRecipient] = useState(EVERYONE);
  // A direct message falls back to everyone once that person has left (a
  // candidate who drops and rejoins comes back with a new peer id).
  const recipient =
    chosenRecipient.startsWith("peer:") &&
    !remotePeers.some(peer => `peer:${peer.id}` === chosenRecipient)
      ? EVERYONE
      : chosenRecipient;
  const [draft, setDraft] = useState("");
  const messages = useMessages(recipient);
  const bottomRef = useRef(null);

  useEffect(() => {
    hmsActions.setMessageRead(true);
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, hmsActions]);

  const recipientName = useMemo(() => {
    if (recipient.startsWith("peer:")) {
      return remotePeers.find(peer => peer.id === recipient.slice(5))?.name;
    }
    return null;
  }, [recipient, remotePeers]);

  const send = async () => {
    const text = draft;
    if (!text.trim()) {
      return;
    }
    try {
      if (recipient.startsWith("role:")) {
        await hmsActions.sendGroupMessage(text, [recipient.slice(5)]);
      } else if (recipient.startsWith("peer:")) {
        await hmsActions.sendDirectMessage(text, recipient.slice(5));
      } else {
        await hmsActions.sendBroadcastMessage(text);
      }
      setDraft("");
    } catch (error) {
      toast.error("Your message wasn't sent", { description: error?.message });
    }
  };

  return (
    <div className="flex h-full flex-col">
      <SidePaneHeader
        closeLabel="Close chat"
        closeTestId="close_chat"
        onClose={onClose}
      />
      <div className="border-b px-4 py-3">
        <Select value={recipient} onValueChange={setRecipient}>
          <SelectTrigger
            className="w-full"
            aria-label="Send to"
            data-testid="chat_recipient_select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EVERYONE}>Everyone</SelectItem>
            {roles.length ? (
              <SelectGroup>
                <SelectLabel>Roles</SelectLabel>
                {roles.map(role => (
                  <SelectItem key={role} value={`role:${role}`}>
                    {role}
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : null}
            {remotePeers.length ? (
              <SelectGroup>
                <SelectLabel>Participants</SelectLabel>
                {remotePeers.map(peer => (
                  <SelectItem key={peer.id} value={`peer:${peer.id}`}>
                    {peer.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : null}
          </SelectContent>
        </Select>
      </div>
      <PinnedMessage />
      <ScrollArea className="min-h-0 flex-1">
        {messages.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            There are no messages here
          </p>
        ) : (
          <ul className="flex flex-col py-2">
            {messages.map(message => (
              <Message
                key={message.id}
                message={message}
                localPeerId={localPeerId}
                canPin={permissions?.removeOthers}
              />
            ))}
          </ul>
        )}
        <div ref={bottomRef} />
      </ScrollArea>
      <form
        className="flex items-end gap-2 border-t p-3"
        onSubmit={event => {
          event.preventDefault();
          send();
        }}
      >
        <Textarea
          rows={1}
          value={draft}
          placeholder={
            recipientName ? `Message ${recipientName}` : "Write something here"
          }
          aria-label="Message"
          className="max-h-32 min-h-9 resize-none"
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            event.stopPropagation();
            // Safari confirms an IME composition with an Enter whose
            // isComposing is false but keyCode is 229.
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing &&
              event.nativeEvent.keyCode !== 229
            ) {
              event.preventDefault();
              send();
            }
          }}
        />
        <Button
          type="submit"
          size="icon"
          aria-label="Send message"
          data-testid="send_msg_btn"
          disabled={!draft.trim()}
        >
          <SendHorizontal />
        </Button>
      </form>
    </div>
  );
};
